import type { IMedia, IOption, ITarget, Question } from 'shared-domains';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import map from 'lodash/map';
import unescape from 'lodash/unescape';
import isArray from 'lodash/isArray';
import forEach from 'lodash/forEach';
import { QuestionTypes } from '~/config/Constants/EditorConstants';
import Constants from '~/config/Constants';
import FeatureFlags from '~/services/FeatureFlagsService';

/**
 * @description Replaces custom <blank></blank> tag with span to create a styled blank effect
 * @param {string} queryStringHTML
 * @param {{withStyles: boolean, withRomans: boolean, blankClass: string, content: Object, placeholder: string, click: Function}} config
 * @returns {string} a string with all the blanks replaced by span with styling
 */
export function replaceBlankWithStyledSpan(
  queryStringHTML: string,
  {
    withStyles = true,
    withRomans = true,
    blankClass = '',
    placeholder = '&nbsp;',
    content = {} as Record<string, string>,
    style = {},
    singleBlankClass = [],
  } = {},
) {
  const blankHTML = queryStringHTML.match(/<blank.*?<\/blank>/g);

  const blankElements = map(blankHTML, (blankEle: string, index: number) => {
    const notation = ['(a)', '(b)', '(c)', '(d)', '(e)'];
    const blankId = blankEle.match(/".*"/)![0].replace(/['"]+/g, '');
    let blankStyles: Record<string, string | number> = {
      'display': 'inline-block',
      'width': '61px',
      'border-bottom': '1px solid #222222',
      'line-height': 1,
      ...style,
    };

    if (!withStyles) {
      blankStyles = {};
    }

    const flattenStyles = Object.keys(blankStyles)
      .map(key => `${key}:${blankStyles[key]}`)
      .join(';');
    let replacedBlankElement = `<span class="question-query-blank ${blankClass} ${singleBlankClass[index] ?? ''
    }" id="${blankId}" style="${flattenStyles}">${content[blankId] ?? placeholder
    }</span>`;
    if (withRomans) {
      replacedBlankElement = `${notation[index]} ${replacedBlankElement}`;
    }
    return ` ${replacedBlankElement} `;
  });

  let blankHtmlString = queryStringHTML;

  forEach(blankHTML, (blank, index) => {
    blankHtmlString = blankHtmlString.replace(blank, blankElements[index]);
  });

  return blankHtmlString;
}

/**
 * @descriptions creates a map of target id to option id or vice versa to avoid O(N) lookups
 * @param {[{targetId: string, optionId: [string]}]} answer
 * @param {boolean} canOptionHaveMultipleTargets
 * @returns {object} with a key value pairs of option/target to its target/option
 */
export function mapTargetIdToOptionId(
  answer: any,
  canOptionHaveMultipleTargets: boolean = false,
) {
  const answerMap: Record<string, Record<string, boolean>> = {};

  if (!isArray(answer)) {
    return;
  }

  if (canOptionHaveMultipleTargets) {
    answer.forEach((ans) => {
      ans.optionId?.forEach((optionId: string) => {
        if (!answerMap[optionId]) {
          answerMap[optionId] = {};
        }
        if (!answerMap[optionId][ans.targetId]) {
          answerMap[optionId][ans.targetId] = true;
        }
      });
    });
  } else {
    answer.forEach((ans) => {
      ans.optionId?.forEach((optionId: string) => {
        if (!answerMap[ans.targetId]) {
          answerMap[ans.targetId] = {};
        }
        if (!answerMap[ans.targetId][optionId]) {
          answerMap[ans.targetId][optionId] = true;
        }
      });
    });
  }

  return answerMap;
}

export function getHtmlWithMathKatexString(mathJaxHTML: string, katex: any) {
  const mathJaxElements = mathJaxHTML.match(/<katex.*?<\/katex>/g);
  const katexElements = map(mathJaxElements, (mathJaxEle) => {
    const latexStr = mathJaxEle.match(/latex.*?=.*?".*?"/)?.[0];
    const quotedLatex = latexStr?.match(/".*"/)?.[0];
    const latex = unescape(quotedLatex?.substring(1, quotedLatex.length - 1));

    let katexEle = '';

    try {
      katexEle = katex.renderToString(latex, {
        displayMode: true,
      });
    } catch (err) {
      window.console.error(err);
    }

    return ` ${katexEle} `;
  });
  let katexHTML = mathJaxHTML;

  forEach(mathJaxElements, (mathJaxEle, index) => {
    katexHTML = katexHTML.replace(mathJaxEle, katexElements[index]);
  });

  return katexHTML;
}

export function isQuestionForPoll(question: Question) {
  if (!question) {
    return false;
  }

  return (
    question.type === QuestionTypes.POLL
    || [QuestionTypes.MCQ, QuestionTypes.MSQ].includes(question.type)
  )
      && get(question, 'structure.settings.hasCorrectAnswer') === false;
}

export function isWordCloudQuestion(question: Question) {
  if (!question) {
    return false;
  }

  return (
    question.type === QuestionTypes.WORDCLOUD
    || (
      QuestionTypes.OPEN === question.type
      && get(question, 'structure.settings.questionDisplayVariant') === Constants.DisplayVariantNames[QuestionTypes.WORDCLOUD]
    )
  );
}

export function getHtmlToRender(text: string, katex?: any) {
  let parsedText = text || '';

  if (parsedText.includes('<blank')) {
    parsedText = replaceBlankWithStyledSpan(parsedText);
  }

  // check for math
  if (parsedText.toLowerCase().includes('<katex')) {
    return katex && typeof katex.renderToString === 'function' ? getHtmlWithMathKatexString(parsedText, katex) : '';
  }

  /** In case white text color is applied it wont be seen on the tile */
  const regex = /color: white/g;
  return parsedText.replace(regex, 'color: #222');
}

// #region Question query
export function getQuestionText(question: Question, katex: any) {
  return getHtmlToRender(get(question, 'structure.query.text', ''), katex);
}

export function doesQuestionHaveMath(question: Question) {
  return get(question, 'structure.query.text', '').toLowerCase().includes('<katex');
}
// #endregion Question query

// --------------------------------------------------------------------------------------------------------------------

// #region Question Media
export function doesQuestionHaveMedia(question: Question): boolean {
  return get(question, 'structure.query.media.length', 0) > 0;
}

export function getQuestionMedia(question: Question): IMedia | null {
  if (!doesQuestionHaveMedia(question)) {
    return null;
  }

  return get(question, 'structure.query.media', [])[0];
}

export function doesQuestionHaveImageMedia(question: Question) {
  if (!doesQuestionHaveMedia(question)) {
    return false;
  }

  return getQuestionMedia(question)?.type === 'image';
}

export function doesQuestionHaveAudioMedia(question: Question) {
  if (!doesQuestionHaveMedia(question)) {
    return false;
  }

  return getQuestionMedia(question)?.type === 'audio';
}

export function doesQuestionHaveVideoMedia(question: Question) {
  if (!doesQuestionHaveMedia(question)) {
    return false;
  }

  return getQuestionMedia(question)?.type === 'video';
}
// #endregion Question Media

// --------------------------------------------------------------------------------------------------------------------

// #region Question Options
export function getQuestionOptions(question: Question): IOption[] {
  return get(question, 'structure.options', []);
}

export function getQuestionMatches(question: Question): IOption[] {
  return get(question, 'structure.matches', []);
}

export function getQuestionTargets(question: Question): ITarget[] {
  return get(question, 'structure.targets', []);
}

export function doesQuestionAllowCustomResponse(question: Question) {
  return get(question, 'question.structure.settings.canSubmitCustomResponse', false);
}
// #endregion Question Options

// --------------------------------------------------------------------------------------------------------------------

// #region Question Answer
export function getQuestionAnswer(question: Question) {
  const multiAnswerTypes = [Constants.QuestionTypes.MSQ,
    Constants.QuestionTypes.REORDER,
    Constants.QuestionTypes.MATCH,
    Constants.QuestionTypes.DND_IMAGE,
  ];
  const targetBasedAnswerTypes = [Constants.QuestionTypes.DRAGNDROP, Constants.QuestionTypes.DROPDOWN];

  if (multiAnswerTypes.includes(question.type)) {
    return get(question, 'structure.answer', []);
  }

  if (targetBasedAnswerTypes.includes(question.type)) {
    return mapTargetIdToOptionId(get(question, 'structure.answer', []), true);
  }

  return get(question, 'structure.answer', null);
}
// #endregion Question Options

// --------------------------------------------------------------------------------------------------------------------

// #region Answer explanation
export function doesQuestionHaveAnswerExplanation(question: Question, katex: any) {
  const hasText = !isEmpty(getAnswerExplanationText(question, katex));
  const hasMedia = !isEmpty(getAnswerExplanationMedia(question));

  return !isEmpty(get(question, 'structure.explain', {})) && (hasText || hasMedia);
}

export function getAnswerExplanationText(question: Question, katex: any) {
  return getHtmlToRender(get(question, 'structure.explain.text', ''), katex);
}

export function doesAnswerExplanationHaveMedia(question: Question): boolean {
  return !isEmpty(getAnswerExplanationMedia(question));
}

export function getAnswerExplanationMedia(question: Question): IMedia | null {
  return get(question, 'structure.explain.media', [])[0];
}

export function doesAnswerExplanationHaveImageMedia(question: Question) {
  if (!doesAnswerExplanationHaveMedia(question)) {
    return false;
  }

  return getAnswerExplanationMedia(question)?.type === 'image';
}

export function doesAnswerExplanationHaveAudioMedia(question: Question) {
  if (!doesAnswerExplanationHaveMedia(question)) {
    return false;
  }

  return getAnswerExplanationMedia(question)?.type === 'audio';
}

export function doesAnswerExplanationHaveVideoMedia(question: Question) {
  if (!doesAnswerExplanationHaveMedia(question)) {
    return false;
  }

  return getAnswerExplanationMedia(question)?.type === 'video';
}
// #endregion Answer explanation

// #region Tags
export function doesQuestionHaveTags(question: Question) {
  return get(question, 'topics.length', 0) > 0;
}

export function getQuestionTags(question: Question) {
  if (!doesQuestionHaveTags(question)) {
    return [];
  }

  return get(question, 'topics', []);
}

export function checkVideoComprehensionType(question?: Question) {
  return question?.type === Constants.QuestionTypes.COMPREHENSION_VIDEO;
}

export function isParentQuestion(question: Question) {
  return [Constants.QuestionTypes.COMPREHENSION_VIDEO, Constants.QuestionTypes.COMPREHENSION].includes(question.type);
}

export function isVideoQuestionEnabled() {
  return FeatureFlags().isEnabled(Constants.FeatureFlagsTypes.QFW_VIDEO_QT);
}

export function isVideoQuestionTimestampsEnabled() {
  return FeatureFlags().isEnabled(Constants.FeatureFlagsTypes.VIDEO_QT_TIMESTAMP);
}
// #endregion Tags

export function getQuizCreatedById(createdBy: any, defaultValue: string | undefined = undefined) {
  return (createdBy._id ?? createdBy.id) ?? defaultValue;
}

export function shouldDisableSettingForComprehension(settingId: string) {
  return [
    Constants.GameOptions.MYSTIC_PEAK,
    Constants.GameOptions.ADAPTIVE,
  ].includes(settingId);
};

export function doesQuestionHaveCorrectAnswer(question: Question) {
  const questionTypeHavingNoCorrectAnswers = [
    Constants.QuestionTypes.OPEN,
    Constants.QuestionTypes.DRAW,
    Constants.QuestionTypes.AUDIO,
    Constants.QuestionTypes.VIDEO,
  ];

  return !questionTypeHavingNoCorrectAnswers.includes(question.type);
}
